/**
 * Talent Invitation hooks — invitation-first acquisition flow.
 *
 * Used by:
 *   - journey-web AcceptInvitePage: usePreviewInvitation + useAcceptInvitation
 *   - business-web Pipeline + Add Talents wizard: list/create/bulk/send/resend/revoke/delete
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useSdkContext } from '../providers/SdkProvider';
import { homeKeys } from './home-keys';
import {
  createTalentInvitationsService,
  type AcceptAskedTest,
  type AcceptJourneyCandidate,
  type AcceptJourneyChoice,
  type AcceptJourneyPreview,
  type AcceptTalentInvitationInput,
  type CreateInvitationInput,
  type BulkImportInput,
  type InviteTemplateCode,
} from '../services/talent-invitations.service';

// ── Query keys ───────────────────────────────────────────────────────

export const talentInvitationKeys = {
  all: ['talent-invitations'] as const,
  preview: (token: string) => [...talentInvitationKeys.all, 'preview', token] as const,
  /**
   * Per-token eligibility for the SIGNED-IN caller. Keyed by token only: the
   * identity is implicit in the session, and the query is invalidated after the
   * citizen verifies a new address so the decision is re-derived.
   */
  eligibility: (token: string) => [...talentInvitationKeys.all, 'eligibility', token] as const,
  /** Citizen first run · D-1 — the share-or-fresh preview for one token. */
  acceptPreview: (token: string) => [...talentInvitationKeys.all, 'accept-preview', token] as const,
  /**
   * Invitations waiting for the signed-in citizen (no token involved). The
   * bare key is a prefix of the full list's, so invalidating it refreshes both.
   */
  mine: () => [...talentInvitationKeys.all, 'mine'] as const,
  /** Every pending invitation, the ones put off with "Not now" included. */
  mineAll: () => [...talentInvitationKeys.all, 'mine', 'all'] as const,
  // Business-side list scoped by filters. Hierarchical: talentInvitationKeys.list()
  // is a strict prefix that invalidates ALL filtered variants: a key with a
  // trailing `undefined` would NOT match a cached filtered key, so a shorter
  // array is the only way to hit every variant.
  list: (filters?: {
    cohortId?: string;
    programId?: string;
    includeTerminal?: boolean;
    includeClosed?: boolean;
  }) =>
    filters === undefined
      ? ([...talentInvitationKeys.all, 'list'] as const)
      : ([...talentInvitationKeys.all, 'list', filters] as const),
  /** Per-row detail key (used by the InvitationDetailDrawer). */
  detail: (id: string) => [...talentInvitationKeys.all, 'detail', id] as const,
};

// ── Citizen-facing ───────────────────────────────────────────────────

/**
 * Public preview — fetched on the AcceptInvitePage without auth.
 * Returns sanitized invitation metadata so the citizen can see the
 * inviting company / opportunity / expiry BEFORE signing in.
 */
export function usePreviewInvitation(token: string | null | undefined) {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);

  return useQuery({
    queryKey: talentInvitationKeys.preview(token ?? ''),
    queryFn: () => {
      if (!token) throw new Error('Missing invitation token');
      return service.preview(token);
    },
    enabled: !!token,
    // Token responses are stable (status only changes via accept/expire/revoke
    // which we'd refetch on user action) — long staleness is fine.
    staleTime: 60_000,
    retry: false, // 410 Gone / 404 Not Found should NOT retry — they're terminal
  });
}

/**
 * Invitations waiting for the signed-in citizen.
 *
 * Query-driven, unlike every other invitation hook here: the whole premise of
 * reconciliation is that this person never received the token, because they
 * signed up directly instead of clicking the link.
 */
export function useMyPendingInvitations(enabled = true) {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);

  return useQuery({
    queryKey: talentInvitationKeys.mine(),
    queryFn: () => service.mine(),
    enabled,
    // An invitation can be revoked or expire between visits, and this drives a
    // prompt asking someone to join a company — offering a stale one would be
    // worse than offering none.
    staleTime: 30_000,
  });
}

/**
 * Every invitation waiting for the signed-in citizen — the ones put off with
 * "Not now" included, each with its `deferredUntil`. The citizen's Invitations
 * list: "Not now" quiets an invitation, it never takes it out of reach.
 */
export function useMyInvitations(enabled = true) {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);

  return useQuery({
    queryKey: talentInvitationKeys.mineAll(),
    queryFn: () => service.mine({ includeDeferred: true }),
    enabled,
    staleTime: 30_000,
  });
}

/** "Not now". Records that they were asked; the prompt returns next visit. */
export function useDeferInvitation() {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => service.defer(token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: talentInvitationKeys.mine() });
      // Home lists the invitation among what needs the citizen.
      void queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/**
 * Pre-flight eligibility for the accept page.
 *
 * The page used to know only `isSignedIn`, so it offered a fully-enabled
 * "Accept invitation" button to anyone — including a citizen whose identity
 * could not possibly accept, for whom the click was guaranteed to 403. This is
 * what lets it know before it offers.
 *
 * `staleTime: 0` on purpose, unlike the preview beside it. The answer changes
 * the moment the citizen verifies a new address, and serving a cached "you
 * cannot accept" right after they have earned the right to would recreate the
 * dead end this whole flow exists to remove.
 */
export function useInvitationEligibility(token: string | null | undefined, enabled = true) {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);

  return useQuery({
    queryKey: talentInvitationKeys.eligibility(token ?? ''),
    queryFn: () => {
      if (!token) throw new Error('Missing invitation token');
      return service.eligibility(token);
    },
    enabled: !!token && enabled,
    staleTime: 0,
    // A terminal invitation is terminal; retrying only delays the explanation.
    retry: false,
  });
}

/**
 * Accept the invitation — triggered from the AcceptInvitePage CTA
 * after the citizen has authenticated via Clerk. Server-side checks
 * email match + email_verified.
 */
export function useAcceptInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (input: AcceptTalentInvitationInput) => service.accept(input),
    onSuccess: () => {
      // After acceptance the citizen has a new journey + talent — invalidate
      // their journey list so the new card surfaces, and the pending list so
      // the invitation card leaves the dashboard.
      queryClient.invalidateQueries({ queryKey: ['journeys'] });
      queryClient.invalidateQueries({ queryKey: ['journeyInstances'] });
      queryClient.invalidateQueries({ queryKey: talentInvitationKeys.mine() });
      // A new journey moves Home to another moment, with its own cards.
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/**
 * Claim an invitation sent to ANOTHER Tonnie account the citizen holds — send
 * the code to the invited address. (Citizen first run · S5)
 */
export function useStartInvitationClaim() {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);
  return useMutation({ mutationFn: (token: string) => service.claimStart(token) });
}

/**
 * Enter the claim code. On success the invitation is the signed-in account's:
 * eligibility is re-derived (it now answers `can_accept`, naming what happens to
 * the other account), and the pending list picks the invitation up.
 */
export function useVerifyInvitationClaim() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);
  return useMutation({
    mutationFn: (input: { token: string; code: string }) =>
      service.claimVerify(input.token, input.code),
    onSuccess: async (_data, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: talentInvitationKeys.eligibility(input.token) }),
        queryClient.invalidateQueries({ queryKey: talentInvitationKeys.mine() }),
      ]);
    },
  });
}

/**
 * Which of my journeys may I share with this company — or build a new one?
 * (R5). The server lists the choosable journeys and suggests one; the accept
 * surfaces render the list. `staleTime: 0`: the answer moves the moment a
 * journey is built, archived or sponsored, and a cached list could offer a
 * journey the acceptance would refuse.
 */
export function useAcceptJourneyPreview(token: string | null | undefined, enabled = true) {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);

  return useQuery({
    queryKey: talentInvitationKeys.acceptPreview(token ?? ''),
    queryFn: () => {
      if (!token) throw new Error('Missing invitation token');
      return service.acceptPreview(token);
    },
    enabled: !!token && enabled,
    staleTime: 0,
    retry: false,
  });
}

/**
 * The accept chooser's state, for both accept surfaces (the dashboard
 * invitation card and `/accept-invite`) and the phone's review sheet:
 *
 *   • `choose`  — the journeys the citizen may share and whether a new one may
 *                 be built. The SERVER's suggestion is preselected (R5); with
 *                 nothing to share, a new journey for the company is.
 *   • `tests`   — a TestIt invitation, accepted as TESTS (D3): the asked tests,
 *                 each with whether a result is already banked. No journey is
 *                 chosen and no choice is sent — the server attaches it.
 *   • `blocked` — nothing to share and no new journey allowed (every journey on
 *                 the route belongs to another company's sponsorship, and the
 *                 route is at its cap): nothing can be accepted.
 *   • `error`   — the preview failed. Accept is withheld rather than sent
 *                 blind: the server would pick, and the citizen was not asked.
 */
export type AcceptJourneyChooserState =
  | { kind: 'loading' }
  | {
      kind: 'choose';
      company: string | null;
      candidates: AcceptJourneyCandidate[];
      canCreate: boolean;
      cap: number;
      selected: AcceptJourneyChoice;
      /**
       * Accepting lets the company read the CheckUp (`grants` holds `checkup`)
       * — the only case a surface may say "will see your CheckUp".
       */
      sharesCheckup: boolean;
    }
  | {
      kind: 'tests';
      company: string | null;
      tests: AcceptAskedTest[];
      /** Nothing is left to take: accepting shares the results that exist. */
      completed: boolean;
      /**
       * The relationship holds the CheckUp once accepted — only a re-invite onto
       * one that already does. A TestIt relationship is born without it.
       */
      sharesCheckup: boolean;
    }
  | { kind: 'blocked'; cap: number }
  | { kind: 'error' };

export interface AcceptJourneyChooser {
  state: AcceptJourneyChooserState;
  select: (choice: AcceptJourneyChoice) => void;
  /** Accept may be pressed: the preview answered with something to land on. */
  ready: boolean;
  /**
   * The choice to send with the acceptance. `undefined` while not `ready` — and
   * for a TestIt invitation, which chooses no journey: the server attaches it.
   */
  journeyChoice: AcceptJourneyChoice | undefined;
  retry: () => void;
}

/** What the chooser reads: the preview query and the citizen's own pick, if any. */
export interface AcceptJourneyChooserInput {
  enabled: boolean;
  isLoading: boolean;
  isError: boolean;
  data: AcceptJourneyPreview | undefined;
  picked: AcceptJourneyChoice | null;
}

/** Two choices are the same choice. */
export function sameAcceptJourneyChoice(a: AcceptJourneyChoice, b: AcceptJourneyChoice): boolean {
  return 'new' in a ? 'new' in b : 'journeyId' in b && a.journeyId === b.journeyId;
}

/**
 * The decision itself, pure — shared by both apps through
 * `useAcceptJourneyChooser` and testable without a query client.
 */
export function deriveAcceptJourneyChooser(
  input: AcceptJourneyChooserInput
): Pick<AcceptJourneyChooser, 'state' | 'ready' | 'journeyChoice'> {
  const state = ((): AcceptJourneyChooserState => {
    if (!input.enabled || input.isLoading) return { kind: 'loading' };
    if (input.isError || !input.data) return { kind: 'error' };
    const { candidates, suggestedJourneyId, canCreate, cap, company } = input.data;
    // D3 — a TestIt invitation is accepted as tests. Blocked only when the
    // server has no journey to rest it on and may build none.
    if (input.data.journeyChoice === 'server' && input.data.testit) {
      if (!suggestedJourneyId && !canCreate) return { kind: 'blocked', cap };
      return {
        kind: 'tests',
        company: company?.name ?? null,
        tests: input.data.testit.tests,
        completed: input.data.testit.completed,
        sharesCheckup: input.data.grants.includes('checkup'),
      };
    }
    const suggested: AcceptJourneyChoice | null = suggestedJourneyId
      ? { journeyId: suggestedJourneyId }
      : canCreate
        ? { new: true }
        : null;
    // The citizen's own pick stands only while it is still on offer.
    const stillOffered = (c: AcceptJourneyChoice) =>
      'new' in c ? canCreate : candidates.some((j) => j.id === c.journeyId);
    const selected = input.picked && stillOffered(input.picked) ? input.picked : suggested;
    if (!selected) return { kind: 'blocked', cap };
    return {
      kind: 'choose',
      company: company?.name ?? null,
      candidates,
      canCreate,
      cap,
      selected,
      sharesCheckup: input.data.grants.includes('checkup'),
    };
  })();

  return {
    state,
    ready: state.kind === 'choose' || state.kind === 'tests',
    journeyChoice: state.kind === 'choose' ? state.selected : undefined,
  };
}

/**
 * @param token   the invitation's token.
 * @param enabled ask only once this identity can accept.
 */
export function useAcceptJourneyChooser(
  token: string | null | undefined,
  enabled: boolean
): AcceptJourneyChooser {
  const preview = useAcceptJourneyPreview(token, enabled);
  const [picked, setPicked] = useState<AcceptJourneyChoice | null>(null);
  const { refetch } = preview;
  const retry = useCallback(() => void refetch(), [refetch]);

  return {
    ...deriveAcceptJourneyChooser({
      enabled,
      isLoading: preview.isLoading,
      isError: preview.isError,
      data: preview.data,
      picked,
    }),
    select: setPicked,
    retry,
  };
}

/**
 * Decline the invitation — the citizen-facing counterpart to accept. Terminal +
 * idempotent server-side; invalidates the preview so the page re-renders the
 * declined state.
 */
export function useDeclineInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (token: string) => service.decline(token),
    onSuccess: (_data, token) => {
      queryClient.invalidateQueries({ queryKey: talentInvitationKeys.preview(token) });
      // A declined invitation is no longer waiting: the dashboard card and the
      // mobile sheet read `mine`, and would otherwise keep offering it.
      queryClient.invalidateQueries({ queryKey: talentInvitationKeys.mine() });
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

// ── Business-facing ──────────────────────────────────────────────────

export function useInvitations(filters?: {
  cohortId?: string;
  programId?: string;
  includeTerminal?: boolean;
  /** Adds the declined and expired invitations the company can act on. */
  includeClosed?: boolean;
}) {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);

  return useQuery({
    queryKey: talentInvitationKeys.list(filters),
    queryFn: () => service.list(filters),
    // If the API was 500ing during dev (e.g. mid-deploy schema drift) and
    // the user pressed import → wizard close, the cache could otherwise
    // stay in a failed-retry state and the column would render empty
    // even after the API recovered. These defaults force a fresh fetch
    // every time the Pipeline page mounts and refetches whenever the
    // user comes back to the tab.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    // Smart polling — match `useTalentList` so both columns refetch on
    // the same cadence. 60s is hot enough to feel live without
    // hammering the API.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    // Cap retries at 1 — invitations is not a hot read, and a stale
    // failure stays around for 30s of retry-backoff under defaults
    // (3 attempts), which masks recent recoveries.
    retry: 1,
  });
}

/**
 * Single-row fetch for the InvitationDetailDrawer in business-web.
 * The drawer mounts whenever an admin clicks an invitation card on
 * the Pipeline (Acquisition column) or anywhere else through the
 * shared TalentDetailDispatcher.
 *
 * `enabled` is gated on a real id so closed drawers don't fire a
 * request. The query key is hierarchical under
 * `talentInvitationKeys.all` so invalidations from create / send /
 * resend / revoke / delete cascade automatically.
 */
export function useInvitation(invitationId: string | null | undefined) {
  const { client } = useSdkContext();
  const service = createTalentInvitationsService(client);

  return useQuery({
    queryKey: talentInvitationKeys.detail(invitationId ?? ''),
    queryFn: () => {
      if (!invitationId) throw new Error('Missing invitationId');
      return service.get(invitationId);
    },
    enabled: !!invitationId,
    // The drawer renders a live status pill + timeline. Stay fresh
    // but don't hammer — 30s matches the drawer's "feels live"
    // expectation without per-mount refetch storms.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Every company view that draws an invitation's card: the invitation lists,
 * the Pipeline and lens rosters (`business/talents`), each cohort's roster
 * snapshot, the cohorts' counts and the stage counts. Every invitation write
 * refreshes all of them. Invite again and Revoke used to refresh only the
 * lists, so a cohort board kept the card "Declined" after it was sent again
 * (4 Oct 2026). The `business` prefixes are written out: `businessKeys` lives
 * in `useBusiness`, which imports this file.
 */
export function invalidateInvitationViews(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: talentInvitationKeys.list() });
  void queryClient.invalidateQueries({ queryKey: ['business', 'talents'] });
  void queryClient.invalidateQueries({ queryKey: ['business', 'cohort-snapshot'] });
  void queryClient.invalidateQueries({ queryKey: ['business', 'cohorts'] });
  void queryClient.invalidateQueries({ queryKey: ['business', 'pipeline-stats'] });
}

export function useCreateInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (input: CreateInvitationInput) => service.create(input),
    onSuccess: () => invalidateInvitationViews(queryClient),
  });
}

export function useBulkImportInvitations() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (input: BulkImportInput) => service.bulkImport(input),
    onSuccess: () => invalidateInvitationViews(queryClient),
  });
}

/**
 * Send (or resend) an invitation. Accepts an optional opportunity
 * payload (destination / branch / scenario / message) collected by the
 * Send-to-Vetting modal — persisted on the invitation row before the
 * email goes out and propagated to the journey_instance on accept.
 *
 * Two call shapes:
 *   - `mutate(invitationId)` — send without changing opportunity
 *     fields (used by Resend path)
 *   - `mutate({ id, opportunity })` — send with opportunity tweaks
 */
export function useSendInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (
      input:
        | string
        | {
            id: string;
            opportunity?: {
              destinationCountry?: string | null;
              branchName?: string | null;
              workLocation?: string | null;
              scenario?:
                | 'pre_immigration'
                | 'already_abroad'
                | 'relocation_only'
                | 'integration_only'
                | null;
              customMessage?: string | null;
              customSubject?: string | null;
              customBody?: string | null;
              customBodySource?: InviteTemplateCode | null;
              invitationKind?: 'full_checkup' | 'testit' | null;
              testitTests?: string[] | null;
              maxResultAgeDays?: number | null;
            };
          }
    ) => {
      if (typeof input === 'string') return service.send(input);
      return service.send(input.id, input.opportunity);
    },
    onSuccess: () => invalidateInvitationViews(queryClient),
  });
}

export function useResendInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (id: string) => service.resend(id),
    onSuccess: () => invalidateInvitationViews(queryClient),
  });
}

export function useRevokeInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (id: string) => service.revoke(id),
    onSuccess: () => invalidateInvitationViews(queryClient),
  });
}

/**
 * Delete invitation (Acquisition column "Delete" action) — the ONLY delete a
 * company has on a lead. Optimistic removal so the card disappears instantly;
 * it rolls back if the server rejects.
 */
export function useDeleteInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTalentInvitationsService(client);

  return useMutation({
    mutationFn: (id: string) => service.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: talentInvitationKeys.list() });
      const snapshots = queryClient.getQueriesData<unknown>({
        queryKey: talentInvitationKeys.list(),
      });
      queryClient.setQueriesData<unknown>(
        { queryKey: talentInvitationKeys.list() },
        (old: unknown) => {
          if (!Array.isArray(old)) return old;
          return (old as Array<{ id?: string }>).filter((x) => x?.id !== id);
        }
      );
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => invalidateInvitationViews(queryClient),
  });
}
